/**
 * SurgeryTab.tsx
 *
 * The patient chart's Surgery tab — the V5 port of legacy pageID=29
 * (html/html/lib/patient-details/consult-details.php).
 *
 * Plan-scoped, matching legacy's patientID + planID + pageID triple. Saves send
 * only the field that changed, so two people working the same checklist no
 * longer overwrite each other; the server merges and appends the history
 * snapshot that the per-field audit stamps are replayed from.
 */
import { Fragment, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Printer } from 'lucide-react';
import DOMPurify from 'dompurify';
import { Alert, Button, Checkbox, ConfirmDialog, Input, Select, Textarea } from '@/components/ui';
import { toast } from '@/store/toastStore';
import { getErrorMessage } from '@/services/apiClient';
import { formatCurrency, formatDate, formatDateTime } from '@/lib/utils';
import { formatPacificDate, formatPacificDateTime } from '@/lib/facilityTimezone';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { useAuth } from '@/hooks/useAuth';
import { getPlansByPatient } from '@/services/plans.service';
import { SURGERY_RETRY_OR_SUPPORT } from '@/pages/surgery/surgeryShared';
import { SurgeryFilesCard } from './surgery/SurgeryFilesCard';
import {
  getSurgeryRecord,
  printSurgeryRecord,
  removeFromSurgeryList,
  setSpecialSituation,
  saveSurgeryRecord,
  type SurgeryAuditStamp,
  type SurgeryRecord,
} from '@/services/surgeryRecord.service';
import {
  CHECKLIST_FIELDS,
  CONSULT_STATUS_CHOICES,
  CRNA_CLEARANCE_CHOICES,
  IMPLANT_FIELDS,
  NOTE_FIELDS,
  formatCoordinatorNote,
  parseCoordinatorNote,
  visibleOptions,
  type ChecklistField,
  type CoordinatorNote,
} from './surgery/surgeryChecklist';

interface SurgeryTabProps {
  patientId: number;
  planId?: number;
  /** Switches the chart to another tab — used by the Labs link. */
  onOpenSection?: (section: string) => void;
}

/**
 * "Last updated Sep 15, 2026 5:47 PM EDT by Jane Doe", or "Not updated". The
 * stamp is stored as a Pacific wall clock and is shown in the viewer's zone.
 */
function AuditText({ stamp }: { stamp: SurgeryAuditStamp | null | undefined }) {
  const userTz = useUserTimezone();
  if (!stamp) {
    return <em className="text-xs text-slate-400">Not updated</em>;
  }

  return (
    <em className="text-xs text-slate-500">
      Last updated {formatPacificDateTime(stamp.at, userTz)}
      {stamp.by ? ` by ${stamp.by}` : ''}
    </em>
  );
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <h3 className="border-b border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-900">
        {title}
      </h3>
      <div className="p-4">{children}</div>
    </section>
  );
}

export function SurgeryTab({ patientId, planId: routedPlanId, onOpenSection }: SurgeryTabProps) {
  // Stamps (Last updated, Removed on) are Pacific wall clocks shown in the
  // viewer's zone; Pre-op and Procedure use the facility's instead.
  const userTz = useUserTimezone();

  // Legacy put a plan selector at the top of the tab, because a patient can
  // carry several plans and only one of them is the surgical case being worked.
  const {
    data: patientPlans,
    isLoading: plansLoading,
    isError: plansFailed,
    error: plansError,
  } = useQuery({
    queryKey: ['patient-plans', patientId],
    queryFn: () => getPlansByPatient(patientId),
    enabled: Number.isFinite(patientId) && patientId > 0,
  });

  const [chosenPlanId, setChosenPlanId] = useState<number | undefined>(routedPlanId);

  // Fall back to the patient's only plan when the URL carries none.
  const planId =
    chosenPlanId ?? routedPlanId ?? (patientPlans?.length === 1 ? patientPlans[0].planId : undefined);

  const queryClient = useQueryClient();
  const [pendingField, setPendingField] = useState<string | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['surgery-record', planId],
    queryFn: () => getSurgeryRecord(planId as number),
    enabled: Boolean(planId),
  });

  const save = useMutation({
    mutationFn: (changes: Record<string, string | number>) =>
      saveSurgeryRecord(planId as number, changes),
    onSuccess: () => {
      // The worklist shows these same values, so keep it honest.
      void queryClient.invalidateQueries({ queryKey: ['surgery-list'] });
      // Returned so a cleared draft falls back to the refreshed value, not the
      // one from before the save.
      return queryClient.invalidateQueries({ queryKey: ['surgery-record', planId] });
    },
    onError: (err) => {
      toast.error('Could not save your change', getErrorMessage(err, SURGERY_RETRY_OR_SUPPORT));
    },
    onSettled: () => setPendingField(null),
  });

  // Consult Status and CRNA Clearance save only from their Save button, as
  // legacy's save icons do, so a stray pick sends nothing. A pick is held here
  // until then, keyed by plan so it never follows the user to another plan.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const draftKey = (field: string) => `${planId}:${field}`;

  const { user } = useAuth();
  // Legacy shows this option only above user level 4; the server checks it too.
  const canRemove = (user?.userLevel ?? 0) > 4;
  const [confirmRemove, setConfirmRemove] = useState(false);

  const remove = useMutation({
    mutationFn: () => removeFromSurgeryList(planId as number),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['surgery-record', planId] });
      queryClient.invalidateQueries({ queryKey: ['surgery-list'] });
      toast.success('Removed from the surgery list');
    },
    onError: (err) => {
      toast.error(
        'Could not take this case off the surgery list',
        getErrorMessage(err, SURGERY_RETRY_OR_SUPPORT),
      );
    },
  });

  const special = useMutation({
    mutationFn: ({ itemId, checked }: { itemId: number; checked: boolean }) =>
      setSpecialSituation(planId as number, itemId, checked),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['surgery-record', planId] }),
    onError: (err) =>
      toast.error('Could not save your change', getErrorMessage(err, SURGERY_RETRY_OR_SUPPORT)),
  });

  const [printing, setPrinting] = useState(false);

  const commit = (field: string, value: string | number) => {
    setPendingField(field);
    save.mutate({ [field]: value });
  };

  const record = data as SurgeryRecord | undefined;

  const values = useMemo<Record<string, string>>(() => {
    if (!record) return {};

    const consult = record.consult;
    const out: Record<string, string> = {
      labsReviewed: consult?.labsReviewed ?? '',
      clearance: consult?.clearance ?? '',
      CRNAClearance: consult?.crnaClearance ?? '',
      status: consult?.status ?? '',
      leftImplant: consult?.leftImplant ?? '',
      rightImplant: consult?.rightImplant ?? '',
      medicalClearanceNote: consult?.notes.medicalClearance ?? '',
      CRNANote: consult?.notes.crna ?? '',
      surgeonsNote: consult?.notes.surgeon ?? '',
    };

    for (const [key, value] of Object.entries(record.checklist ?? {})) {
      out[key] = String(value);
    }

    return out;
  }, [record]);

  const coordinator = useMemo(
    () => parseCoordinatorNote(record?.consult?.notes.coordinator ?? ''),
    [record],
  );

  /**
   * Re-pack all three coordinator values on every save. They share one column,
   * so writing a part in isolation would blank the others.
   */
  const saveCoordinator = (patch: Partial<CoordinatorNote>) => {
    const next = { ...coordinator, ...patch };
    if (formatCoordinatorNote(next) === formatCoordinatorNote(coordinator)) return;
    commit('coordinatorsNote', formatCoordinatorNote(next));
  };

  const draftFor = (field: 'status' | 'CRNAClearance') =>
    drafts[draftKey(field)] ?? values[field] ?? '';

  const removedOn = record?.consult?.endDate ?? null;
  // Remove end-dates the plan's open consult row. A plan with no consult row has
  // nothing to take off the list (the server answers 404), so offer it only on an
  // open case.
  const hasOpenCase = Boolean(record?.consult) && !removedOn;

  /**
   * Legacy's status save icon also puts a removed case back on the list, so on
   * a removed case the current status can be saved unchanged.
   */
  const canSave = (field: 'status' | 'CRNAClearance') =>
    draftFor(field) !== (values[field] ?? '') || (field === 'status' && Boolean(removedOn));

  const saveDraft = (field: 'status' | 'CRNAClearance') => {
    const key = draftKey(field);
    const value = draftFor(field);
    if (!canSave(field)) return;
    setPendingField(field);
    save.mutate(
      { [field]: value },
      {
        onSuccess: () =>
          setDrafts((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
          }),
      },
    );
  };

  const planOptions = (patientPlans ?? []).map((p) => ({
    value: String(p.planId),
    label: [p.displayId ?? p.planId, p.doctorName, p.stageName].filter(Boolean).join(' — '),
  }));

  const planSelector =
    planOptions.length > 1 ? (
      <div className="flex items-center gap-2 text-sm max-md:w-full">
        <label htmlFor="surgery-plan" className="text-slate-500">
          Plan
        </label>
        <Select
          id="surgery-plan"
          options={planOptions}
          value={planId ? String(planId) : null}
          placeholder="Choose a plan..."
          onChange={(v) => setChosenPlanId(Number(v) || undefined)}
          wrapperClassName="w-64 max-md:w-auto max-md:min-w-0 max-md:flex-1"
          className="touch-target-h"
        />
      </div>
    ) : null;

  const print = async () => {
    if (!planId) return;
    setPrinting(true);
    try {
      await printSurgeryRecord(planId);
    } catch (err) {
      toast.error('Could not print this case', getErrorMessage(err, SURGERY_RETRY_OR_SUPPORT));
    } finally {
      setPrinting(false);
    }
  };

  const header = (
    <div className="flex items-center justify-between max-md:flex-wrap max-md:gap-2">
      <h2 className="text-lg font-semibold text-slate-900">Surgery</h2>
      <div className="flex items-center gap-3 max-md:w-full max-md:flex-wrap">
        {planSelector}
        {planId && data ? (
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Printer className="h-4 w-4" />}
            loading={printing}
            onClick={() => void print()}
          >
            Print
          </Button>
        ) : null}
      </div>
    </div>
  );

  const errorBanner = (message: string) => (
    <div
      role="alert"
      className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700"
    >
      {message}
    </div>
  );

  // No plan chosen yet: still offer the picker, or the tab is a dead end.
  if (!planId) {
    return (
      <div className="space-y-4">
        {header}
        {plansLoading ? (
          <p className="text-sm text-slate-400">Loading plans…</p>
        ) : plansFailed ? (
          errorBanner(
            getErrorMessage(
              plansError,
              "Could not load this patient's plans. Refresh the page, and if it keeps happening, contact Prospera support.",
            ),
          )
        ) : (
          <p className="py-4 text-sm text-slate-400 italic">
            {planOptions.length > 1
              ? 'Choose a plan to see its surgical case.'
              : 'This patient has no plan with a surgical case.'}
          </p>
        )}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        {header}
        <p className="text-sm text-slate-400">Loading surgical case…</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-4">
        {header}
        {errorBanner(
          getErrorMessage(
            error,
            "Could not load this surgical case. Refresh the page, and if it keeps happening, contact Prospera support.",
          ),
        )}
      </div>
    );
  }

  const renderRow = (field: ChecklistField) => {
    const value = values[field.key] ?? '';
    const busy = pendingField === field.key;
    const controlId = `surgery-field-${field.key}`;

    return (
      <tr key={field.key} className="border-b border-slate-100 last:border-0 max-md:block max-md:py-2">
        <th className="w-56 px-3 py-2 text-left text-sm font-medium text-slate-700 align-middle max-md:block max-md:w-full max-md:px-0 max-md:py-1">
          <label htmlFor={controlId}>{field.label}</label>
        </th>
        <td className="w-56 px-3 py-2 max-md:block max-md:w-full max-md:px-0 max-md:py-1">
          {field.kind === 'select' ? (
            <Select
              id={controlId}
              options={visibleOptions(field.options, value).map((o) => ({
                value: o.value,
                label: o.label,
              }))}
              value={value}
              placeholder="Select one..."
              disabled={busy}
              onChange={(v) => commit(field.key, v ?? '')}
              className="touch-target-h"
            />
          ) : (
            <Input
              key={`${planId}:${field.key}`}
              id={controlId}
              type="text"
              className="touch-target-h"
              defaultValue={value}
              disabled={busy}
              aria-label={field.label}
              onBlur={(e) => {
                if (e.target.value !== value) commit(field.key, e.target.value);
              }}
            />
          )}
        </td>
        <td className="px-3 py-2 max-md:block max-md:w-full max-md:px-0 max-md:py-1">
          <AuditText stamp={record?.audit?.[field.key]} />
        </td>
      </tr>
    );
  };

  const plan = record?.plan;

  const vitals = record?.vitals;

  return (
    <div className="space-y-4">
      {header}
      {vitals ? (
        <SectionCard title="Vitals">
          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2 md:grid-cols-4">
            {([
              ['Age', vitals.age],
              ['BMI', vitals.bmi],
              ['Height', vitals.height],
              ['Blood Pressure', vitals.bloodPressure],
              ['Weight', vitals.weight],
              ['Heart Rate', vitals.heartRate],
              ['Added', vitals.added ? formatDate(vitals.added) : ''],
              ['Temperature', vitals.temperature],
            ] as const).map(([label, value]) => (
              <div key={label}>
                <dt className="text-xs text-slate-500">{label}</dt>
                <dd className="text-slate-900">{value || '—'}</dd>
              </div>
            ))}
          </dl>
        </SectionCard>
      ) : null}

      {plan ? (
        <SectionCard title={plan.planDisplayId ? `Plan #${plan.planDisplayId}` : 'Plan'}>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2 md:grid-cols-4">
            <div>
              <dt className="text-xs text-slate-500">Pre-op Date</dt>
              <dd className="text-slate-900">
                {formatPacificDateTime(plan.preopDate, plan.facilityTimezone ?? '')}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500">Procedure Date</dt>
              <dd className="text-slate-900">
                {formatPacificDateTime(plan.procedureDate, plan.facilityTimezone ?? '')}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500">Plan Type</dt>
              <dd className="text-slate-900">{plan.planType || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500">Stage</dt>
              <dd className="text-slate-900">{plan.stage || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500">Doctor</dt>
              <dd className="text-slate-900">{plan.doctorName || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500">Coordinator</dt>
              <dd className="text-slate-900">{plan.coordinatorName || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500">Nurse</dt>
              <dd className="text-slate-900">{plan.nurseName || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500">Balance Due</dt>
              <dd className="text-slate-900">{formatCurrency(plan.due)}</dd>
            </div>
          </dl>
          {plan.procedures ? (
            <p className="mt-4 border-t border-slate-100 pt-3 text-sm text-slate-700">
              {plan.procedures}
            </p>
          ) : null}
        </SectionCard>
      ) : null}

      <SectionCard title="Checklist">
        <div className="overflow-x-auto">
          <table className="min-w-full max-md:block">
            <tbody className="max-md:block">
              {CHECKLIST_FIELDS.filter((f) => !f.hiddenRow).map((field) => (
                <Fragment key={field.key}>
                  {renderRow(field)}
                  {/* Legacy puts the money row immediately after Mammogram. */}
                  {field.key === 'mammogram' && record?.balance ? (
                    <tr className="border-b border-slate-100 max-md:block max-md:py-2">
                      <th className="w-56 px-3 py-2 text-left text-sm font-medium text-slate-700 max-md:block max-md:w-full max-md:px-0 max-md:py-1">
                        {record.balance.label}
                      </th>
                      <td className="w-56 px-3 py-2 text-sm text-slate-900 max-md:block max-md:w-full max-md:px-0 max-md:py-1">
                        {formatCurrency(record.balance.amount)}
                      </td>
                      <td className="px-3 py-2 max-md:block max-md:w-full max-md:px-0 max-md:py-1">
                        {record.balance.lastPaidOn ? (
                          <em className="text-xs text-slate-500">
                            Last paid {formatDate(record.balance.lastPaidOn)}
                          </em>
                        ) : (
                          <em className="text-xs text-slate-400">Not updated</em>
                        )}
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

      <SectionCard title="Implants Chosen">
        <div className="overflow-x-auto">
          <table className="min-w-full max-md:block">
            <tbody className="max-md:block">{IMPLANT_FIELDS.map(renderRow)}</tbody>
          </table>
        </div>
      </SectionCard>

      {record?.specialSituations?.length ? (
        <SectionCard title="Special Situations">
          <ul className="divide-y divide-slate-100">
            {record.specialSituations.map((item) => (
              <li key={item.id} className="flex items-center gap-3 py-2 max-md:flex-wrap max-md:gap-y-1">
                <Checkbox
                  id={`special-${item.id}`}
                  label={item.title}
                  checked={item.checked}
                  disabled={special.isPending}
                  onChange={(e) =>
                    special.mutate({ itemId: item.id, checked: e.target.checked })
                  }
                  wrapperClassName="flex-1"
                />
                {item.at ? (
                  <em className="text-xs text-slate-500 max-md:basis-full max-md:pl-7">
                    Last updated {formatPacificDate(item.at, userTz)}
                    {item.by ? ` by ${item.by}` : ''}
                  </em>
                ) : (
                  <em className="text-xs text-slate-400 max-md:basis-full max-md:pl-7">Not updated</em>
                )}
              </li>
            ))}
          </ul>
        </SectionCard>
      ) : null}

      <SectionCard title="Consult Status">
        {removedOn ? (
          <Alert variant="warning" className="mb-4">
            Removed from the Surgery list on {formatPacificDate(removedOn, userTz)}. Save
            a consult status to put it back.
          </Alert>
        ) : null}
        <div className="flex flex-wrap items-center gap-6">
          {([
            ['status', 'Consult Status', CONSULT_STATUS_CHOICES],
            ['CRNAClearance', 'CRNA Clearance', CRNA_CLEARANCE_CHOICES],
          ] as const).map(([field, label, choices]) => {
            const draft = draftFor(field);
            const busy = pendingField === field;
            return (
              <div key={field} className="flex flex-col gap-1">
                <div className="flex items-end gap-2">
                  <Select
                    id={`surgery-${field}`}
                    label={label}
                    options={visibleOptions(choices, draft).map((o) => ({
                      value: o.value,
                      label: o.label,
                    }))}
                    value={draft}
                    placeholder="Select one..."
                    disabled={busy}
                    onChange={(v) =>
                      setDrafts((prev) => ({ ...prev, [draftKey(field)]: v ?? '' }))
                    }
                    wrapperClassName="w-48"
                    className="touch-target-h"
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    aria-label={`Save ${label}`}
                    disabled={busy || !canSave(field)}
                    loading={busy}
                    onClick={() => saveDraft(field)}
                  >
                    Save
                  </Button>
                </div>
                <AuditText stamp={record?.audit?.[field]} />
              </div>
            );
          })}

          {canRemove && hasOpenCase ? (
            <div className="ml-auto self-end">
              <Button variant="secondary" size="sm" onClick={() => setConfirmRemove(true)}>
                Remove from Surgery List
              </Button>
            </div>
          ) : null}
        </div>
      </SectionCard>

      <ConfirmDialog
        isOpen={confirmRemove}
        onClose={() => setConfirmRemove(false)}
        onConfirm={() => remove.mutateAsync()}
        title="Remove from the surgery list?"
        message="This case will come off the surgery list. Its checklist and history are kept. Save a consult status to put it back."
        confirmLabel="Remove"
        variant="warning"
      />

      {NOTE_FIELDS.map((note) => (
        <SectionCard key={note.key} title={note.label}>
          <Textarea
            key={`${planId}:${note.key}`}
            className="min-h-24"
            defaultValue={values[note.key] ?? ''}
            aria-label={note.label}
            onBlur={(e) => {
              if (e.target.value !== (values[note.key] ?? '')) commit(note.key, e.target.value);
            }}
          />
          <div className="mt-2">
            <AuditText stamp={record?.audit?.[note.key]} />
          </div>
        </SectionCard>
      ))}

      {record?.history?.map((section) => (
        <SectionCard key={section.title} title={section.title}>
          <dl className="grid gap-x-6 gap-y-3 text-sm md:grid-cols-2 max-md:grid-cols-1">
            {section.fields.map((f) => (
              <div key={f.label} className={f.type === 'text' ? 'md:col-span-2' : undefined}>
                <dt className="text-xs text-slate-500">{f.label}</dt>
                <dd className="whitespace-pre-wrap text-slate-900 max-md:break-words">{f.value}</dd>
              </div>
            ))}
          </dl>
        </SectionCard>
      ))}

      <SectionCard title="Labs">
        {/*
          Legacy embedded the whole lab browser here (quest-lab-list.php, ~3.7k
          lines: Documents / Biomarkers / History / Quest / Vibrant). V5 already
          has that surface as the patient's own Labs tab, so this links to it
          rather than maintaining a second copy that would drift.
          "Labs Checked" on the checklist above is the field this tab owns.
        */}
        <p className="text-sm text-slate-600">
          Lab results, biomarkers and orders live on this patient's Labs tab.
        </p>
        <div className="mt-3">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onOpenSection?.('labs')}
          >
            Open Labs
          </Button>
        </div>
      </SectionCard>

      <SurgeryFilesCard patientId={patientId} />

      {record?.commLog?.length ? (
        <SectionCard title="Consult Comm Log">
          <ul className="divide-y divide-slate-100">
            {record.commLog.map((m) => (
              <li key={m.commLogId} className="py-3">
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="text-sm font-medium text-slate-900">
                    {m.subject || (m.isStageEvent ? 'Stage change' : 'Message')}
                  </span>
                  {m.isStageEvent ? (
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">
                      Stage
                    </span>
                  ) : null}
                  {m.hasAttachments ? (
                    <span className="text-xs text-slate-500">Has attachments</span>
                  ) : null}
                  <span className="ml-auto text-xs text-slate-500">
                    {m.author ?? ''}
                    {m.sentAt ? ` · ${formatDateTime(m.sentAt)}` : ''}
                  </span>
                </div>
                {m.body ? (
                  <div
                    className="mt-1 text-sm text-slate-700 max-md:overflow-x-auto max-md:break-words"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(m.body) }}
                  />
                ) : null}
              </li>
            ))}
          </ul>
        </SectionCard>
      ) : null}

      <SectionCard title="Coordinator's Note">
        {/*
          All three values live in one column, separated by the literal markers
          legacy still parses, so any save has to re-pack the whole trio rather
          than write one part.
        */}
        <Textarea
          key={`${planId}:note-${coordinator.note}`}
          className="min-h-24"
          defaultValue={coordinator.note}
          aria-label="Coordinator's Note"
          onBlur={(e) => saveCoordinator({ note: e.target.value })}
        />
        <div className="mt-3 grid gap-3 md:grid-cols-2 max-md:grid-cols-1">
          <Input
            key={`${planId}:zoom-${coordinator.zoomLink}`}
            id="surgery-zoom-link"
            label="Zoom Link"
            type="text"
            className="touch-target-h"
            defaultValue={coordinator.zoomLink}
            onBlur={(e) => saveCoordinator({ zoomLink: e.target.value })}
          />
          <Input
            key={`${planId}:subject-${coordinator.subject}`}
            id="surgery-coordinator-subject"
            label="Subject"
            type="text"
            className="touch-target-h"
            defaultValue={coordinator.subject}
            onBlur={(e) => saveCoordinator({ subject: e.target.value })}
          />
        </div>
        <div className="mt-2">
          <AuditText stamp={record?.audit?.coordinatorsNote} />
        </div>
      </SectionCard>
    </div>
  );
}
